import React, {Fragment, useContext, useEffect, useState} from "react";
import * as Sentry from '@sentry/browser';
import {Redirect} from "react-router-dom";
import {withAuth} from "../../hoc/Auth";
import {FormattedMessage, useIntl} from "react-intl";
import Api from "../../Api";
import Icon from "../Utils/Icon";
import classes from "./styles.module.css";
import MessageContext from "../../MessageContext";
import clearSiteDataImg from "./cache-clear.png";
import logo from '../Logo/images/e-clear_logo.png';

const LoginForm = withAuth(
    ({isAuthorized, adminPrivileges, forcedPasswordChange, isRemember, authorize, remember}) => {
        const intl = useIntl();
        const {onMessage} = useContext(MessageContext);

        const [email, setEmail] = useState("");
        const [psw, setPsw] = useState("");
        const [code, setCode] = useState("");

        const [isMFA, setIsMFA] = useState(false);
        const [mfaData, setMFAData] = useState({OTPProvider: "", OTPEmail: "", OTPPhoneNumber: ""});
        const [validTime, setValidTime] = useState(0);
        const [isTimeLeft, setIsTimeLeft] = useState(false);

        const [modalType, setModalType] = useState(null);
        const [recoveryEmail, setRecoveryEmail] = useState("");
        const [recoveryCode, setRecoveryCode] = useState("");
        const [recoveryStep, setRecoveryStep] = useState(1); // 1-email, 2-code
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            var timer;
            if (isMFA && validTime > 0) {
                timer = setTimeout(function () {
                    setValidTime(validTime - 1);
                }, 1000);
            } else if (isMFA && validTime === 0) {
                setIsTimeLeft(true);
            }
            return function () {
                clearTimeout(timer);
            };
        }, [validTime, isMFA]);

        function login() {
            if (!email.trim() || !psw.trim()) {
                onMessage({
                    type: "warning",
                    message: intl.formatMessage({id: "LoginForm.error.empty"}),
                });
                return;
            }

            new Api()
                .login({username: email, password: psw, code: code})
                .then(function (r) {
                    if (r && r.token) {
                        authorize(r.token, isRemember);
                    } else if (r && r.mfa) {
                        setIsMFA(true);
                        setMFAData(r);
                        setValidTime(r.OTPCodeTimeLeft || 120);
                    }
                })
                .catch(function (err) {
                    Sentry.captureException(err);
                    // Заблокированной учётке честно говорим, в чём дело: иначе
                    // человек будет без конца перебирать верный пароль.
                    const reason = err && err.response && err.response.data && err.response.data.reason;
                    onMessage({
                        type: "error",
                        message: intl.formatMessage({
                            id: reason === "account_blocked" ? "LoginForm.error.blocked" : "LoginForm.error.credentials",
                        }),
                    });
                });
        }

        function submitHandler(e) {
            e.preventDefault();
            login();
        }

        function sendPasswordRecovery() {
            if (!recoveryEmail.trim()) {
                onMessage({
                    type: "warning",
                    message: intl.formatMessage({id: "LoginForm.recovery.error.emailRequired"}),
                });
                return;
            }

            setLoading(true);

            new Api()
                .passwordResetRequest(recoveryEmail)
                .then(function () {
                    onMessage({
                        type: "success",
                        message: intl.formatMessage({id: "LoginForm.recovery.success.codeSent"}),
                    });
                    setRecoveryStep(2);
                })
                .catch(function (err) {
                    Sentry.captureException(err);
                    onMessage({
                        type: "error",
                        message: intl.formatMessage({id: "LoginForm.recovery.error.sendCode"}),
                    });
                })
                .finally(function () {
                    setLoading(false);
                });
        }

        function verifyRecoveryCode() {
            if (!recoveryCode.trim()) {
                onMessage({
                    type: "warning",
                    message: intl.formatMessage({id: "LoginForm.recovery.error.codeRequired"}),
                });
                return;
            }

            setLoading(true);

            new Api()
                .passwordResetVerify(recoveryEmail, recoveryCode)
                .then(function (r) {
                    if (r && r.success) {
                        onMessage({
                            type: "success",
                            message: intl.formatMessage({id: "LoginForm.recovery.success.passwordSent"}),
                        });
                        setModalType(null);
                        setRecoveryStep(1);
                        setRecoveryEmail("");
                        setRecoveryCode("");
                    } else {
                        onMessage({
                            type: "error",
                            message: r.message || intl.formatMessage({id: "LoginForm.recovery.error.invalidCode"}),
                        });
                    }
                })
                .catch(function (err) {
                    Sentry.captureException(err);
                    onMessage({
                        type: "error",
                        message: intl.formatMessage({id: "LoginForm.recovery.error.verifyCode"}),
                    });
                })
                .finally(function () {
                    setLoading(false);
                });
        }

        function sendAgain() {
            setIsTimeLeft(false);
            setValidTime(120);
            login();
        }

        if (isAuthorized) {
            if (adminPrivileges) return <Redirect to="/console"/>;
            if (forcedPasswordChange) return <Redirect to="/profile"/>;
            return <Redirect to="/"/>;
        }

        return (
            <Fragment>
                <form onSubmit={submitHandler}>
                    <img
                        className={classes.Logo}
                        width={"90"}
                        height={"90"}
                        src={logo}
                        alt={intl.formatMessage({id: "LoginForm.logo.alt"})}
                    ></img>

                    <h2 className={classes.Title}>
                        <FormattedMessage id="LoginForm.title.login" defaultMessage="Вход в личный кабинет"/>
                    </h2>

                    {isMFA && (
                        !isTimeLeft ? (
                            <Fragment>
                                <p className={classes.Text}>
                                    {mfaData.OTPProvider === "email" && (
                                        <FormattedMessage
                                            id="LoginForm.text.mfaEmailMessage"
                                            values={{
                                                type: <b>{mfaData.OTPProvider}</b>,
                                                email: <b>{mfaData.OTPEmail}</b>,
                                            }}
                                            defaultMessage="Введите код подтверждения из {type}-сообщения, отправленного на {email}"
                                        />
                                    )}
                                    {mfaData.OTPProvider === "sms" && (
                                        <FormattedMessage
                                            id="LoginForm.text.mfaPhoneMessage"
                                            values={{
                                                type: <b>{mfaData.OTPProvider}</b>,
                                                phone: <b>{mfaData.OTPPhoneNumber}</b>,
                                            }}
                                            defaultMessage="Введите код подтверждения из {type}-сообщения, отправленного на номер {phone}"
                                        />
                                    )}
                                    {mfaData.OTPProvider === "gauth" && (
                                        <FormattedMessage id="LoginForm.text.mfaGAuthMessage"
                                                          defaultMessage="Введите код подтверждения из приложения Google Authenticator"/>
                                    )}
                                </p>

                                <FormattedMessage id="LoginForm.placeholder.code" defaultMessage="Код">
                                    {(placeholder) => (
                                        <input
                                            type="text"
                                            className={classes.Input}
                                            autoComplete="off"
                                            placeholder={placeholder}
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                        />
                                    )}
                                </FormattedMessage>

                                <button className={classes.Btn}>
                                    <FormattedMessage id="LoginForm.btn.continue" defaultMessage="Продолжить"/>
                                </button>

                                {validTime > 0 && (
                                    <span className={classes.Time}>
                                      <FormattedMessage
                                          id="LoginForm.text.codeValid"
                                          defaultMessage="Код действителен еще {seconds} секунд"
                                          values={{seconds: validTime}}
                                      />
                                    </span>
                                )}
                            </Fragment>
                        ) : (
                            <Fragment>
                                <p className={classes.TimeLeft}>
                                    <FormattedMessage id="LoginForm.text.codeExpired"
                                                      defaultMessage="Срок действия кода подтверждения истек."/>
                                </p>
                                <button onClick={sendAgain} className={classes.Btn}>
                                    <FormattedMessage id="LoginForm.btn.sendAgain" defaultMessage="Выслать еще раз"/>
                                </button>
                            </Fragment>
                        )
                    )}

                    {!isMFA && (
                        <Fragment>
                            <FormattedMessage id="LoginForm.placeholder.email" defaultMessage="Имя учетной записи">
                                {(placeholder) => (
                                    <input
                                        className={classes.Input}
                                        placeholder={placeholder}
                                        value={email}
                                        onChange={function (e) {
                                            setEmail(e.target.value);
                                        }}
                                    />
                                )}
                            </FormattedMessage>

                            <FormattedMessage id="LoginForm.placeholder.password" defaultMessage="Пароль">
                                {(placeholder) => (
                                    <input
                                        type="password"
                                        className={classes.Input}
                                        placeholder={placeholder}
                                        value={psw}
                                        onChange={function (e) {
                                            setPsw(e.target.value);
                                        }}
                                    />
                                )}
                            </FormattedMessage>

                            <div className={classes.Row}>
                                <div className={classes.FormCheck}>
                                    <input
                                        id="rememberMe"
                                        type="checkbox"
                                        checked={!!isRemember}
                                        onChange={remember}
                                    />
                                    <label htmlFor="rememberMe" className={classes.Checkbox}>
                                        <div className={classes.Square}>
                                            <span>
                                                <Icon icon="checkmark" size="13px"/>
                                            </span>
                                        </div>
                                        <span>{intl.formatMessage({id: "LoginForm.label.remember"})}</span>
                                    </label>
                                </div>
                            </div>

                            <button className={classes.Btn}>
                                {intl.formatMessage({id: "LoginForm.btn.login"})}
                            </button>

                            <div className={classes.HelpLinks}>
                                <button
                                    type="button"
                                    onClick={function () {
                                        setModalType("forgot");
                                    }}
                                    className={classes.LinkBtn}
                                >
                                    {intl.formatMessage({id: "LoginForm.link.forgotPassword"})}
                                </button>
                                <button
                                    type="button"
                                    onClick={function () {
                                        setModalType("cant");
                                    }}
                                    className={classes.LinkBtn}
                                >
                                    {intl.formatMessage({id: "LoginForm.link.cantLogin"})}
                                </button>
                            </div>
                        </Fragment>
                    )}
                </form>

                {modalType && (
                    <div
                        className={classes.ModalOverlay}
                        onClick={function () {
                            setModalType(null);
                        }}
                    >
                        <div
                            className={classes.Modal}
                            onClick={function (e) {
                                e.stopPropagation();
                            }}
                        >
                            <button
                                type="button"
                                className={classes.ModalClose}
                                onClick={function () {
                                    setModalType(null);
                                }}
                                aria-label={intl.formatMessage({id: "LoginForm.modal.close"})}
                            >
                                ×
                            </button>

                            {modalType === "cant" && (
                                <Fragment>
                                    <h1>{intl.formatMessage({id: "LoginForm.modal.cantLogin.title"})}</h1>
                                    <ol className={classes.ModalList}>
                                        <li>{intl.formatMessage({id: "LoginForm.modal.cantLogin.step.clearCache"})}</li>
                                        <li>
                                            {intl.formatMessage({id: "LoginForm.modal.cantLogin.step.clearSiteData"})}
                                            <div className={classes.ScreenshotPlaceholder}>
                                                <img
                                                    src={clearSiteDataImg}
                                                    alt={intl.formatMessage({id: "LoginForm.modal.cantLogin.alt.clearSiteData"})}
                                                />
                                            </div>
                                        </li>
                                        <li>
                                            {intl.formatMessage({id: "LoginForm.modal.cantLogin.step.contactSupport"})}{" "}
                                            <a href="https://t.me/sup_fintech" target="_blank" rel="noreferrer">
                                                https://t.me/sup_fintech
                                            </a>
                                        </li>
                                    </ol>
                                </Fragment>
                            )}

                            {modalType === "forgot" && (
                                <Fragment>
                                    <h1>{intl.formatMessage({id: "LoginForm.modal.forgotPassword.title"})}</h1>

                                    {recoveryStep === 1 && (
                                        <Fragment>
                                            <FormattedMessage
                                                id="LoginForm.modal.forgotPassword.placeholder.email"
                                                defaultMessage="Электронная почта"
                                            >
                                                {(placeholder) => (
                                                    <input
                                                        className={classes.Input}
                                                        placeholder={placeholder}
                                                        value={recoveryEmail}
                                                        onChange={function (e) {
                                                            setRecoveryEmail(e.target.value);
                                                        }}
                                                    />
                                                )}
                                            </FormattedMessage>
                                            <button
                                                type="button"
                                                className={classes.Btn}
                                                disabled={loading}
                                                onClick={sendPasswordRecovery}
                                            >
                                                {intl.formatMessage({id: "LoginForm.modal.forgotPassword.btn.sendCode"})}
                                            </button>
                                        </Fragment>
                                    )}

                                    {recoveryStep === 2 && (
                                        <Fragment>
                                            <FormattedMessage
                                                id="LoginForm.modal.forgotPassword.placeholder.code"
                                                defaultMessage="Код из письма"
                                            >
                                                {(placeholder) => (
                                                    <input
                                                        className={classes.Input}
                                                        placeholder={placeholder}
                                                        value={recoveryCode}
                                                        onChange={function (e) {
                                                            setRecoveryCode(e.target.value);
                                                        }}
                                                    />
                                                )}
                                            </FormattedMessage>
                                            <button
                                                type="button"
                                                className={classes.Btn}
                                                disabled={loading}
                                                onClick={verifyRecoveryCode}
                                            >
                                                {intl.formatMessage({id: "LoginForm.modal.forgotPassword.btn.verifyCode"})}
                                            </button>
                                        </Fragment>
                                    )}
                                </Fragment>
                            )}
                        </div>
                    </div>
                )}
            </Fragment>
        );
    }
);

export default LoginForm;



// WEBPACK FOOTER //
// ./src/Components/LoginForm/index.js